/**
 * Namespace emission for the `ddl-create-object-name` position — the slot
 * where a `CREATE … <object-type> ▮` statement takes the NEW object's name.
 *
 * The object does not exist yet, so nothing from the schema cache belongs
 * here: a table, a column or a data-type name at this slot is always the
 * wrong answer (before this position kind existed the resolvers' default
 * branch flooded the list with exactly those). The only meaningful
 * completions are the NAMESPACES the object can be created in — schemas on
 * MSSQL / PostgreSQL / Oracle, databases on MySQL, and SQLite's two built-in
 * schemas plus whatever is ATTACHed.
 *
 * Shared by all five resolvers so the slot behaves identically everywhere
 * (the only per-engine differences are the namespace vocabulary and which
 * name is the default — both passed in by the caller).
 */

import type { CompletionCandidate } from '@/core/engines';

/**
 * `CREATE [UNIQUE|CLUSTERED|NONCLUSTERED|COLUMNSTORE|FULLTEXT|SPATIAL|BITMAP]…
 * INDEX [IF NOT EXISTS] <partial>▮` — the INDEX-name slot of the position kind.
 *
 * Not every object type in `ddl-create-object-name` accepts a namespace
 * qualifier: an index name is BARE on MSSQL, PostgreSQL and MySQL (the index
 * lives in its table's schema — `CREATE INDEX public.ix ON t` is a syntax
 * error there), while Oracle and SQLite DO accept `schema.index_name`. The
 * classifier is deliberately engine-agnostic, so the three engines that
 * cannot qualify an index name skip the namespace emission for this shape and
 * keep their existing behavior (MySQL's `IF NOT EXISTS` keyword slot).
 */
const CREATE_INDEX_NAME_RE =
  /\bCREATE\s+(?:(?:UNIQUE|CLUSTERED|NONCLUSTERED|COLUMNSTORE|FULLTEXT|SPATIAL|BITMAP)\s+)*INDEX\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:[A-Za-z_][\w$]*)?$/i;

/** `CREATE [OR REPLACE] [CONSTRAINT] TRIGGER <partial>▮` — PostgreSQL's
 *  trigger-name slot. Same story as the index name: a PG trigger belongs to
 *  its table, so `CREATE TRIGGER public.trg ON t` is a syntax error. MSSQL,
 *  MySQL, Oracle and SQLite all accept a qualified trigger name. */
const CREATE_TRIGGER_NAME_RE =
  /\bCREATE\s+(?:OR\s+REPLACE\s+)?(?:CONSTRAINT\s+)?TRIGGER\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:[A-Za-z_][\w$]*)?$/i;

/** True when the statement prefix up to the caret ends at an index-name slot. */
export function isCreateIndexNameSlot(statementPrefix: string): boolean {
  return CREATE_INDEX_NAME_RE.test(statementPrefix);
}

/** True when the statement prefix up to the caret ends at a trigger-name slot. */
export function isCreateTriggerNameSlot(statementPrefix: string): boolean {
  return CREATE_TRIGGER_NAME_RE.test(statementPrefix);
}

export type CreateObjectNamespaceOptions = {
  /**
   * The namespace a new object lands in when the user types a bare name —
   * `dbo` (MSSQL), the first `search_path` entry (PG), the current database
   * (MySQL), the connected user's schema (Oracle), `main` (SQLite). Emitted
   * with `orderHint: 0` so the ranker floats it to the top of the tier, and
   * added to the list when the snapshot does not carry it (a cold cache must
   * still offer the one namespace the user almost certainly wants).
   */
  readonly defaultName?: string;
  /** `'schema'` for MSSQL/PG/Oracle, `'database'` for MySQL/SQLite. */
  readonly kind: 'schema' | 'database';
  /** Candidate `detail` line, e.g. `'Schema'` / `'Database'`. */
  readonly detail: string;
  /** The engine's identifier quoter (`formatIdentifier`). */
  readonly format: (name: string) => string;
};

/**
 * Push one candidate per namespace, de-duplicated case-insensitively, all at
 * tier 4 (the "other visible namespaces" tier the resolvers already use for
 * schemas) with the default namespace first via `orderHint`.
 */
export function emitCreateObjectNamespaces(
  out: CompletionCandidate[],
  names: readonly string[],
  options: CreateObjectNamespaceOptions,
): void {
  const { defaultName, kind, detail, format } = options;
  const defaultKey = defaultName?.toLowerCase();
  const all = [...names];
  if (defaultName !== undefined && !all.some((n) => n.toLowerCase() === defaultKey)) {
    all.push(defaultName);
  }
  const seen = new Set<string>();
  for (const name of all) {
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const isDefault = key === defaultKey;
    out.push({
      label: format(name),
      insertText: format(name),
      isSnippet: false,
      kind,
      tier: 4,
      detail: isDefault ? `${detail} (default for new objects)` : detail,
      filterText: name,
      ...(isDefault ? { orderHint: 0 } : {}),
    });
  }
}
